import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  ArrowRight,
  RefreshCw,
  Wallet,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { recommendationsApi, leadsApi } from "@/lib/crmApi";
import { useSettings } from "@/context/SettingsContext";
import { formatFCFA, getInitials, statutLabel, statutColor } from "@/lib/crmHelpers";

const ICON_BY_TYPE = {
  appel: Phone,
  email: Mail,
  sms: MessageSquare,
  rdv: Calendar,
};

const ACTION_COLOR = {
  appel: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  email: "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300",
  sms: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  rdv: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
};

const RecommendationRow = ({ rec, rank, onLogged, onOpen }) => {
  const Icon = ICON_BY_TYPE[rec.action_type] || Phone;
  const color = ACTION_COLOR[rec.action_type] || ACTION_COLOR.appel;

  const handleQuickAct = (e) => {
    e.stopPropagation();
    // Just open the phone/sms/mail app or navigate to the lead.
    // We do NOT auto-log an action — user logs manually after if it actually happened.
    if (rec.action_type === "appel" && rec.telephone) {
      window.location.href = `tel:${rec.telephone.replace(/\s+/g, "")}`;
    } else if (rec.action_type === "sms" && rec.telephone) {
      window.location.href = `sms:${rec.telephone.replace(/\s+/g, "")}`;
    } else {
      // For email / rdv / fallback : open the lead detail to act there
      window.location.href = `/leads/${rec.lead_id}`;
    }
  };

  return (
    <div
      data-testid={`recommendation-row-${rec.lead_id}`}
      onClick={() => onOpen(rec.lead_id)}
      className="group flex cursor-pointer items-start gap-3 border-b border-border/40 px-4 py-3 transition-colors last:border-b-0 hover:bg-muted/40"
    >
      <div className="flex flex-none flex-col items-center gap-1.5">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[hsl(var(--accent-navy))] text-[10px] font-bold text-white">
          {rank}
        </span>
        <span className={`flex h-7 w-7 items-center justify-center rounded-full ${color}`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-heading text-sm font-semibold">{rec.lead_nom}</span>
          <span
            className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${statutColor(
              rec.statut
            )}`}
          >
            {statutLabel(rec.statut)}
          </span>
          {rec.montant_prevu > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-[hsl(var(--accent-gold))]">
              <Wallet className="h-2.5 w-2.5" />
              {formatFCFA(rec.montant_prevu)}
              {rec.probabilite > 0 && (
                <span className="ml-1 rounded-sm bg-[hsl(var(--accent-navy))]/10 px-1 text-[10px] text-[hsl(var(--accent-navy))]">
                  {rec.probabilite}%
                </span>
              )}
            </span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-sm font-medium text-foreground">
          <ArrowRight className="h-3 w-3 text-[hsl(var(--accent-gold))]" />
          {rec.todo}
        </div>
        {rec.reasons.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {rec.reasons.map((r) => (
              <span
                key={r}
                className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
              >
                {r}
              </span>
            ))}
            {rec.commercial && (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted/60 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-foreground/10 text-[8px] font-bold">
                  {getInitials(rec.commercial)}
                </span>
                {rec.commercial}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col items-end gap-1.5">
        <span
          className="text-[11px] font-bold tracking-wider text-[hsl(var(--accent-navy))]"
          title="Score de priorité (calcul: étape × urgence × valeur)"
        >
          {Math.round(rec.priority_score)} pts
        </span>
        <Button
          size="sm"
          onClick={handleQuickAct}
          data-testid={`recommendation-act-${rec.lead_id}`}
          className="h-7 gap-1 px-2 text-xs"
        >
          <Icon className="h-3 w-3" />
          {rec.action_type === "appel"
            ? "Appeler"
            : rec.action_type === "email"
            ? "Email"
            : rec.action_type === "sms"
            ? "SMS"
            : rec.action_type === "rdv"
            ? "RDV"
            : "Faire"}
        </Button>
      </div>
    </div>
  );
};

export default function NextActionsSection() {
  const navigate = useNavigate();
  const { activeProjectId } = useSettings();
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await recommendationsApi.nextActions(8, activeProjectId);
      setRecs(data || []);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId]);

  return (
    <section data-testid="next-actions-section">
      <div className="mb-4 flex items-end justify-between">
        <div>
          <div className="text-label text-muted-foreground">Aide à la vente</div>
          <h2 className="font-heading mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Sparkles className="h-6 w-6 text-[hsl(var(--accent-gold))]" />
            Prochaines actions recommandées
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Triées par <strong>priorité intelligente</strong> · score × urgence × valeur potentielle
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          data-testid="next-actions-refresh"
          className="gap-1.5 text-xs"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Recalculer
        </Button>
      </div>

      <div className="rounded-xl border border-border/60 bg-card">
        {recs.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-muted-foreground">
            <TrendingUp className="h-7 w-7 opacity-40" />
            <span>Aucun lead actif à recommander pour le moment.</span>
          </div>
        ) : (
          <ol>
            {recs.map((rec, i) => (
              <RecommendationRow
                key={rec.lead_id}
                rec={rec}
                rank={i + 1}
                onLogged={load}
                onOpen={(id) => navigate(`/leads/${id}`)}
              />
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}
